import { useMemo } from 'react';
import type { MeasureMetadata } from '../../../../../api/work-periods-client/work-periods-client.type';
import { useTranslation } from 'react-i18next';
import { useProcessAnalysisStore } from '../../../../../store/process-analysis-store/process-analysis-store';
import { secondaryBase, skyDark } from '../../../../../styles/design-tokens';
import { calculateMetrics, formatValue } from './side-section.helpers';
import type { InfoItem } from './side-section.type';

/**
 * Custom hook that returns metadata for the currently selected measure
 *
 * @returns {MeasureMetadata | null} The metadata for the selected measure, or null if no measure is selected
 */
const useMeasureMetadata = (): MeasureMetadata | null => {
  const selectedDataPoint = useProcessAnalysisStore((state) => state.selectedDataPoint);
  const availableMeasures = useProcessAnalysisStore((state) => state.availableMeasures);

  const measureMetadata = useMemo(() => {
    if (!selectedDataPoint) {
      return null;
    }

    const { measure } = selectedDataPoint;

    return availableMeasures.find((m) => m.measure_name === measure) || null;
  }, [selectedDataPoint, availableMeasures]);

  return measureMetadata;
};

/**
 * Custom hook that returns info items for the side section based on the selected data point
 *
 * @returns {InfoItem[]} Array of info items to display in the side section
 */
const useInfoItems = (): InfoItem[] => {
  const i18n = useTranslation();
  const selectedDataPoint = useProcessAnalysisStore((state) => state.selectedDataPoint);
  const measureMetadata = useMeasureMetadata();

  const infoItems = useMemo(() => {
    if (!selectedDataPoint || !measureMetadata) {
      return [];
    }

    const { value: actualValue, measureData } = selectedDataPoint;
    const isZeroValid = measureMetadata.is_zero_valid ?? false;

    const { averageValue, trendSlope, forecast } = calculateMetrics(measureData, isZeroValid);

    return [
      {
        color: secondaryBase,
        icon: 'adjust',
        label: i18n.t('process_analysis.side_section.actual_value'),
        value: formatValue(actualValue),
      },
      {
        color: skyDark,
        icon: 'bar_chart',
        label: i18n.t('process_analysis.side_section.average'),
        value: formatValue(averageValue),
      },
      {
        color: skyDark,
        icon: 'trending_up',
        label: i18n.t('process_analysis.side_section.trend_slope'),
        value: formatValue(trendSlope),
      },
      {
        color: skyDark,
        icon: 'insights',
        label: i18n.t('process_analysis.side_section.forecast'),
        value: formatValue(forecast),
      },
    ];
  }, [selectedDataPoint, measureMetadata, i18n.t]);

  return infoItems;
};

export { useInfoItems, useMeasureMetadata };
